import React, { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { recruitingAPI } from "../services/recruitingApi";
import {
  APPLICATION_STATUS_CONFIG,
  APPLICATION_VALID_TRANSITIONS,
} from "./StatusControl/configs/applicationConfig";
import SuccessConfirmationDialog from "./StatusControl/SuccessConfirmationDialog";
import RejectionPopover from "./RejectionPopover";
import JustificationPopover from "./JustificationPopover";
import type { ApplicationStatus } from "../types/shared.types";

// Action verbs for buttons (status labels are past tense, buttons should be imperative)
const ACTION_LABELS: Record<string, string> = {
  approved: "Approve",
  applied: "Apply",
  cv_sent: "Send CV",
  matched_ai: "Match (AI)",
  interview_waiting: "Request Interview",
  interview_scheduled: "Schedule Interview",
  after_interview: "Complete Interview",
  success: "Mark Success",
};

interface QuickStatusChangeProps {
  applicationId: number;
  currentStatus: string;
  jobTitle?: string;
  candidateName?: string;
  onStatusChanged?: () => void;
  compact?: boolean;
}

/**
 * Compact transition control for application cards.
 * Shows allowed transitions as buttons/dropdown.
 * Handles rejection popover and justification dialog.
 */
const QuickStatusChange: React.FC<QuickStatusChangeProps> = ({
  applicationId,
  currentStatus,
  jobTitle,
  candidateName,
  onStatusChanged,
  compact = false,
}) => {
  const queryClient = useQueryClient();
  const [showRejection, setShowRejection] = useState(false);
  const [showJustification, setShowJustification] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [successMetadata, setSuccessMetadata] = useState<any>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const allowedTransitions = APPLICATION_VALID_TRANSITIONS[currentStatus] || [];

  const updateMutation = useMutation(
    async (payload: {
      status: ApplicationStatus;
      note?: string;
      override_justification?: string;
      rejection_reason?: string;
      rejection_reason_note?: string;
    }) => {
      if (payload.status === "rejected") {
        return recruitingAPI.rejectApplication(
          applicationId,
          payload.rejection_reason || "other",
          {
            rejection_reason_note: payload.rejection_reason_note,
          },
        );
      }
      return recruitingAPI.updateApplicationStatus(
        applicationId,
        payload.status,
        {
          note: payload.note,
          override_justification: payload.override_justification,
        },
      );
    },
    {
      onSuccess: (data: any) => {
        queryClient.invalidateQueries("applications");
        queryClient.invalidateQueries(["application", applicationId]);
        setShowRejection(false);
        setShowJustification(false);
        setPendingStatus(null);
        onStatusChanged?.();

        // Show success confirmation dialog when _metadata is present (US1-US4)
        if (data?._metadata) {
          setSuccessMetadata(data._metadata);
          setShowSuccessDialog(true);
        }
      },
      onError: (error: Error) => {
        alert(`Failed to update status: ${error.message}`);
      },
    },
  );

  const handleTransition = (targetStatus: string) => {
    if (targetStatus === "rejected") {
      setShowRejection(true);
      return;
    }

    // not_matched_ai → approved requires justification
    if (currentStatus === "not_matched_ai" && targetStatus === "approved") {
      setPendingStatus(targetStatus);
      setShowJustification(true);
      return;
    }

    updateMutation.mutate({
      status: targetStatus as ApplicationStatus,
      note: `Status changed via quick action`,
    });
  };

  const handleReject = (reason: string, note?: string) => {
    updateMutation.mutate({
      status: "rejected" as ApplicationStatus,
      rejection_reason: reason,
      rejection_reason_note: note,
    });
  };

  const handleJustifiedTransition = (justification: string) => {
    if (!pendingStatus) return;
    updateMutation.mutate({
      status: pendingStatus as ApplicationStatus,
      override_justification: justification,
      note: `Override: ${justification}`,
    });
  };

  if (allowedTransitions.length === 0) return null;

  const buttonPadding = compact ? "2px 6px" : "3px 8px";
  const buttonFontSize = compact ? "10px" : "11px";

  return (
    <div style={{ position: "relative" }}>
      <div
        style={{
          display: "flex",
          gap: "4px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {allowedTransitions
          .filter((s) => s !== "rejected")
          .slice(0, compact ? 1 : 3)
          .map((targetStatus) => {
            const config = APPLICATION_STATUS_CONFIG[targetStatus];
            const actionLabel =
              ACTION_LABELS[targetStatus] || config?.label || targetStatus;
            return (
              <button
                key={targetStatus}
                onClick={(e) => {
                  e.stopPropagation();
                  handleTransition(targetStatus);
                }}
                disabled={updateMutation.isLoading}
                title={`Move to ${config?.label || targetStatus}`}
                style={{
                  padding: buttonPadding,
                  fontSize: buttonFontSize,
                  border: `1px solid ${config?.color || "#d1d5db"}`,
                  borderRadius: "4px",
                  backgroundColor: "white",
                  color: config?.color || "#374151",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  fontWeight: 500,
                }}
              >
                {actionLabel}
              </button>
            );
          })}

        {/* Reject button */}
        {allowedTransitions.includes("rejected") && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleTransition("rejected");
            }}
            disabled={updateMutation.isLoading}
            title="Reject application"
            style={{
              padding: buttonPadding,
              fontSize: buttonFontSize,
              border: "1px solid #ef4444",
              borderRadius: "4px",
              backgroundColor: "white",
              color: "#ef4444",
              cursor: "pointer",
              whiteSpace: "nowrap",
              fontWeight: 500,
            }}
          >
            Reject
          </button>
        )}
      </div>

      {/* Rejection Popover */}
      <RejectionPopover
        show={showRejection}
        onClose={() => setShowRejection(false)}
        onReject={handleReject}
        isLoading={updateMutation.isLoading}
        align="left"
      />

      {/* Success Confirmation Dialog (US1-US4 cross-entity feedback) */}
      {showSuccessDialog && successMetadata && (
        <SuccessConfirmationDialog
          open={showSuccessDialog}
          onClose={() => {
            setShowSuccessDialog(false);
            setSuccessMetadata(null);
          }}
          metadata={successMetadata}
          jobTitle={jobTitle}
          candidateName={candidateName}
        />
      )}

      {/* Justification Dialog (for Not Matched → Approved override) */}
      <JustificationPopover
        show={showJustification}
        onClose={() => {
          setShowJustification(false);
          setPendingStatus(null);
        }}
        onSubmit={handleJustifiedTransition}
        isLoading={updateMutation.isLoading}
        align="left"
      />
    </div>
  );
};

export default QuickStatusChange;
